const GRAPHQL_URL = '/graphql';
const REST_BASE = '/wp-json';

/**
 * Execute a REST API request against the WordPress REST API.
 *
 * @param {string} path    REST path (e.g. '/anacrm/v1/registrations/my')
 * @param {Object} options fetch options (method, body, etc.)
 * @return {Promise<Object|null>} Parsed JSON body, or null for 204 responses
 */
export async function apiFetch( path, options = {} ) {
	const { headers: extraHeaders, ...rest } = options;
	const res = await fetch( REST_BASE + path, {
		...rest,
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': window.anacrm?.nonce ?? '',
			...( extraHeaders || {} ),
		},
	} );

	if ( res.status === 204 ) {
		return null;
	}

	const json = await res.json().catch( () => ( {} ) );
	if ( ! res.ok ) {
		throw Object.assign(
			new Error( json.message || `HTTP ${ res.status }` ),
			{ status: res.status, data: json }
		);
	}
	return json;
}

/**
 * Execute a GraphQL query against the WPGraphQL endpoint.
 *
 * @param {string} query     GraphQL query string
 * @param {Object} variables Query variables
 * @return {Promise<Object>} The `data` object from the response
 */
export async function fetchGraphQL( query, variables = {} ) {
	const res = await fetch( GRAPHQL_URL, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': window.anacrm?.nonce ?? '',
		},
		body: JSON.stringify( { query, variables } ),
	} );

	if ( ! res.ok ) {
		throw new Error( `GraphQL network error ${ res.status }` );
	}

	const json = await res.json();
	if ( json.errors?.length ) {
		throw new Error( json.errors[ 0 ].message );
	}

	return json.data;
}

/**
 * Fetch every row of an archive table in one request (see inc/table-data.php).
 * Rows have the same shape the GraphQL nodes used to have.
 *
 * @param {string} type 'person' | 'org' | 'space' | 'administration' | 'project' | 'resource' | 'activity'
 * @return {Promise<Array>} Table rows
 */
export function fetchTableRows( type ) {
	return apiFetch( `/anacrm/v1/table/${ type }` );
}
